import React, { createContext, useContext, useState, useEffect } from 'react';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

// Crear el contexto para el estado de conexión
export const ConnectionContextMemorySpace = createContext();

/**
 * Contexto global para manejar el estado de conexión a internet
 * Proporciona información sobre si el usuario está online/offline
 * y controla la visibilidad del modal de conexión
 */
export const ConnectionContext = ({ children }) => {
  const isOnline = useOnlineStatus();
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [hasShownOfflineModal, setHasShownOfflineModal] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    if (!isOnline && !hasShownOfflineModal) {
      // Mostrar modal cuando se pierde la conexión
      setShowConnectionModal(true);
      setHasShownOfflineModal(true);
    } else if (isOnline && hasShownOfflineModal) {
      // Cuando se restaura la conexión, hacer refresh de la página
      setShowConnectionModal(false);
      // Hacer refresh después de un breve delay para que el usuario vea el cambio
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [isOnline, hasShownOfflineModal]);

  const closeConnectionModal = async () => {
    // Cerrar el modal primero
    setShowConnectionModal(false);
    
    // Validar si aún no hay conexión después de un breve delay
    setTimeout(async () => {
      // Verificar el estado básico de navigator.onLine
      if (!navigator.onLine) {
        // Si no hay conexión, volver a abrir el modal
        setShowConnectionModal(true);
        setHasShownOfflineModal(true);
        return;
      }

      // Hacer una verificación real de conectividad
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 segundos timeout

        const response = await fetch('/favicon.ico', {
          method: 'HEAD',
          cache: 'no-cache',
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // La petición falló, volver a abrir el modal
          setShowConnectionModal(true);
          setHasShownOfflineModal(true);
        }
      } catch (error) {
        // Error en la petición (timeout, network error, etc.)
        // Volver a abrir el modal
        setShowConnectionModal(true);
        setHasShownOfflineModal(true);
      }
    }, 100); // Pequeño delay para permitir que el modal se cierre visualmente
  };

  const retryConnection = async () => {
    setIsRetrying(true);

    // Primero verificar el estado básico de navigator.onLine
    if (!navigator.onLine) {
      // Si navigator.onLine dice que no hay conexión, mantener el modal
      setIsRetrying(false);
      return;
    }

    // Hacer una verificación real de conectividad
    try {
      // Intentar hacer una petición pequeña para verificar conectividad real
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 segundos timeout

      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        // Conexión verificada exitosamente
        setShowConnectionModal(false);
        setHasShownOfflineModal(false);
        setIsRetrying(false);
        
        // Opcional: recargar la página después de verificar conexión
        // window.location.reload();
      } else {
        // La petición falló, mantener el modal
        // Re-renderizar el modal como si fuera la primera vez
        setShowConnectionModal(true);
        setHasShownOfflineModal(true);
        setIsRetrying(false);
      }
    } catch (error) {
      // Error en la petición (timeout, network error, etc.)
      // Re-renderizar el modal como si fuera la primera vez
      setShowConnectionModal(true);
      setHasShownOfflineModal(true);
      setIsRetrying(false);
    }
  };

  return (
    <ConnectionContextMemorySpace.Provider
      value={{
        isOnline,
        showConnectionModal,
        closeConnectionModal,
        retryConnection,
        hasShownOfflineModal,
        isRetrying
      }}
    >
      {children}
    </ConnectionContextMemorySpace.Provider>
  );
};

/**
 * Hook personalizado para usar el contexto de conexión
 * @returns {Object} Estado y funciones del contexto de conexión
 */
export const useConnectionContext = () => {
  const context = useContext(ConnectionContextMemorySpace);
  if (!context) {
    throw new Error('useConnectionContext debe ser usado dentro de ConnectionContext');
  }
  return context;
};
